import { oc } from "@orpc/contract";
import { z } from "zod";
import { Sd849StructuralRuleSchema } from "@erp/shared-types";

/** Lifecycle of an authored config. */
export const sd849ConfigStatusSchema = z.enum(["draft", "shadow", "active"]);

/** A SupplierConfig travels as plain JSON; the backend validates it strictly. */
const configObjectSchema = z.record(z.string(), z.unknown());

const validationSchema = z.object({
  valid: z.boolean(),
  errors: z.array(z.string()),
});

export const sd849AuthoredConfigSchema = z.object({
  id: z.uuid(),
  supplierName: z.string(),
  /** Variant discriminator (default "default"). */
  label: z.string(),
  supplierId: z.uuid().nullable(),
  config: configObjectSchema,
  status: sd849ConfigStatusSchema,
  notes: z.string().nullable(),
  updatedBy: z.uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const sd849SupplierConfigSummarySchema = z.object({
  supplierName: z.string(),
  status: sd849ConfigStatusSchema.nullable(),
  notes: z.string().nullable(),
  updatedAt: z.string().nullable(),
  hasAuthored: z.boolean(),
});

export const listSd849ConfigSuppliersResponseSchema = z.object({
  suppliers: z.array(sd849SupplierConfigSummarySchema),
});

export const getSd849ConfigResponseSchema = z.object({
  supplierName: z.string(),
  authored: sd849AuthoredConfigSchema.nullable(),
});

export const saveSd849ConfigInputSchema = z.object({
  supplierName: z.string().trim().min(1),
  /** Variant to upsert; defaults to "default" (the single-variant case). */
  label: z.string().trim().min(1).optional(),
  config: configObjectSchema,
  /**
   * `updatedAt` of the authored row this edit was based on. When supplied, the
   * server refuses the write if the row moved since — the whole config is
   * replaced here, so a save built on a stale read silently reverts whoever
   * wrote in between. Omit only when there is no stored row yet.
   */
  expectedUpdatedAt: z.string().optional(),
  // No `status`: only `draft` is implemented. Authored configs do not yet
  // override the live parser, so accepting shadow/active would misrepresent
  // reality. Status is (re)introduced with the shadow→active rollout.
  notes: z.string().max(2000).optional(),
});

export const saveSd849ConfigResponseSchema = z.object({
  saved: sd849AuthoredConfigSchema.nullable(),
  validation: validationSchema,
  /** True when the write was refused because the stored row moved (not a validation failure). */
  conflict: z.boolean().optional(),
});

export const testSd849ConfigInputSchema = z.object({
  sampleId: z.uuid(),
  config: configObjectSchema,
});

export const testSd849ConfigResponseSchema = z.object({
  validation: validationSchema,
  ran: z.boolean(),
  reason: z.string().nullable(),
  coverage: configObjectSchema.nullable(),
  warnings: z.array(z.string()),
});

/** Identifies one authored config: the supplier plus its variant label. */
const supplierConfigRefSchema = z.object({
  supplierName: z.string().trim().min(1),
  label: z.string().trim().min(1).optional(),
});

const listSuppliersContract = oc
  .input(z.object({}))
  .output(listSd849ConfigSuppliersResponseSchema)
  .route({
    method: "GET",
    summary: "List suppliers with a committed or authored 849 parsing config",
    description:
      "Lists each supplier that has a claim-response (849) email parsing config, with its authored status, notes and last update time.",
  });

const getConfigContract = oc
  .input(supplierConfigRefSchema)
  .output(getSd849ConfigResponseSchema)
  .route({
    method: "GET",
    summary: "Get the authored + committed 849 config for a supplier",
    description:
      "Returns the authored draft parsing config for one supplier and optional variant label, or null when none has been saved.",
  });

const validateConfigContract = oc
  .input(z.object({ config: configObjectSchema }))
  .output(validationSchema)
  .route({
    summary: "Validate a draft 849 config against the schema",
    description:
      "Checks a claim-response parsing config JSON for schema errors without saving it; returns valid plus a list of error messages.",
  });

const saveConfigContract = oc
  .input(saveSd849ConfigInputSchema)
  .output(saveSd849ConfigResponseSchema)
  .route({
    summary: "Validate and save an authored 849 config (draft store)",
    description:
      "Saves a supplier's parsing config as a draft and appends a version; does not change the live parser. Refuses stale edits via expectedUpdatedAt.",
  });

/** One append-only snapshot of a config. */
export const sd849ConfigVersionSchema = z.object({
  id: z.uuid(),
  versionNumber: z.int().positive(),
  config: configObjectSchema,
  status: sd849ConfigStatusSchema,
  notes: z.string().nullable(),
  changeReason: z
    .enum(["save", "rollback", "promote", "correction"])
    .nullable(),
  createdBy: z.uuid().nullable(),
  createdAt: z.string(),
});

export const listSd849ConfigVersionsResponseSchema = z.object({
  versions: z.array(sd849ConfigVersionSchema),
});

export const rollbackSd849ConfigInputSchema = supplierConfigRefSchema.extend({
  versionNumber: z.int().positive(),
});

const listVersionsContract = oc
  .input(supplierConfigRefSchema)
  .output(listSd849ConfigVersionsResponseSchema)
  .route({
    method: "GET",
    summary: "Version history for an authored 849 config (newest first)",
    description:
      "Lists saved snapshots of a supplier's parsing config with version number, status, change reason, author and timestamp.",
  });

const rollbackConfigContract = oc
  .input(rollbackSd849ConfigInputSchema)
  .output(saveSd849ConfigResponseSchema)
  .route({
    summary:
      "Restore a previous 849 config version (appends a new version; status unchanged)",
    description:
      "Copies an earlier version of a supplier's parsing config back into the draft, recorded as a new rollback version.",
  });

const testConfigContract = oc
  .input(testSd849ConfigInputSchema)
  .output(testSd849ConfigResponseSchema)
  .route({
    summary: "Dry-run a draft 849 config against a stored sample email",
    description:
      "Parses one saved sample claim-response email with an unsaved config and returns validation, field coverage and warnings. Writes nothing.",
  });

export const checkSenderClaimsInputSchema = z.object({
  /** Sender addresses (or patterns) being considered for a supplier's config. */
  addresses: z.array(z.string().trim().min(1)).max(50),
  /** The supplier being edited — excluded from its own conflict list. */
  supplierName: z.string().trim().min(1),
});

export const senderClaimSchema = z.object({
  address: z.string(),
  /** Formal names of OTHER effective configs whose patterns match this address. */
  claimedBy: z.array(z.string()),
});

export const checkSenderClaimsResponseSchema = z.object({
  claims: z.array(senderClaimSchema),
});

export const setSenderPatternsInputSchema = z.object({
  supplierName: z.string().trim().min(1),
  label: z.string().trim().min(1).optional(),
  /** Replaces `emailDetection.patterns` wholesale; merged server-side. */
  patterns: z.array(z.string().trim().min(1)).max(50),
  /**
   * `updatedAt` of the row this edit was based on. Required: server-side merging
   * protects the OTHER fields, but these patterns are still replaced wholesale, so
   * a stale draft would revert a newer routing change.
   */
  expectedUpdatedAt: z.string().optional(),
});

const setSenderPatternsContract = oc
  .input(setSenderPatternsInputSchema)
  .output(saveSd849ConfigResponseSchema)
  .route({
    summary:
      "Replace a supplier's 849 sender addresses (field-scoped, merged server-side)",
    description:
      "Overwrites the sender email patterns used to route claim-response emails to this supplier's draft config, keeping other config fields intact.",
  });

export const setStructuralRulesInputSchema = z.object({
  supplierName: z.string().trim().min(1),
  label: z.string().trim().min(1).optional(),
  /**
   * Replaces `emailIdentification.structuralRules` wholesale; merged
   * server-side. Each rule is the strict `Sd849StructuralRuleSchema` the config
   * save uses, so a misspelt signal is refused here rather than widening a rule.
   */
  rules: z
    .array(
      // A rule with no requirements never matches. Refused on write only: the
      // shared schema also reads stored configs, where such a rule is inert.
      Sd849StructuralRuleSchema.refine(
        (rule) => Object.keys(rule.requires).length > 0,
        { message: "A structural rule needs at least one requirement" },
      ),
    )
    .max(50),
  /** `updatedAt` of the row this edit was based on — see `setSenderPatterns`. */
  expectedUpdatedAt: z.string().optional(),
});

const setStructuralRulesContract = oc
  .input(setStructuralRulesInputSchema)
  .output(saveSd849ConfigResponseSchema)
  .route({
    summary:
      "Replace a supplier's 849 structural rules (field-scoped, merged server-side)",
    description:
      "Overwrites the structural rules the 849 classifier's deterministic tier uses for this supplier, keeping every other config field intact.",
  });

const checkSenderClaimsContract = oc
  .input(checkSenderClaimsInputSchema)
  .output(checkSenderClaimsResponseSchema)
  .route({
    summary:
      "Which other 849 configs already claim these sender addresses (read-only)",
    description:
      "For each candidate sender address, lists other suppliers whose parsing configs already match it, to avoid routing conflicts.",
  });

export const sd849ConfigsContracts = {
  listSuppliers: listSuppliersContract,
  getConfig: getConfigContract,
  validate: validateConfigContract,
  save: saveConfigContract,
  listVersions: listVersionsContract,
  rollback: rollbackConfigContract,
  testAgainstSample: testConfigContract,
  checkSenderClaims: checkSenderClaimsContract,
  setSenderPatterns: setSenderPatternsContract,
  setStructuralRules: setStructuralRulesContract,
};

export type Sd849ConfigStatus = z.infer<typeof sd849ConfigStatusSchema>;
export type Sd849AuthoredConfig = z.infer<typeof sd849AuthoredConfigSchema>;
export type Sd849SupplierConfigSummary = z.infer<
  typeof sd849SupplierConfigSummarySchema
>;
export type ListSd849ConfigSuppliersResponse = z.infer<
  typeof listSd849ConfigSuppliersResponseSchema
>;
export type GetSd849ConfigResponse = z.infer<
  typeof getSd849ConfigResponseSchema
>;
export type SaveSd849ConfigInput = z.infer<typeof saveSd849ConfigInputSchema>;
export type SaveSd849ConfigResponse = z.infer<
  typeof saveSd849ConfigResponseSchema
>;
export type TestSd849ConfigResponse = z.infer<
  typeof testSd849ConfigResponseSchema
>;
export type Sd849ConfigVersion = z.infer<typeof sd849ConfigVersionSchema>;
export type ListSd849ConfigVersionsResponse = z.infer<
  typeof listSd849ConfigVersionsResponseSchema
>;
export type RollbackSd849ConfigInput = z.infer<
  typeof rollbackSd849ConfigInputSchema
>;
export type SetSenderPatternsInput = z.infer<
  typeof setSenderPatternsInputSchema
>;
export type SetStructuralRulesInput = z.infer<
  typeof setStructuralRulesInputSchema
>;
export type CheckSenderClaimsInput = z.infer<
  typeof checkSenderClaimsInputSchema
>;
export type SenderClaim = z.infer<typeof senderClaimSchema>;
export type CheckSenderClaimsResponse = z.infer<
  typeof checkSenderClaimsResponseSchema
>;
